import { useEffect, useState } from 'react'

/**
 * Conversion-focused blocks for /docusign-alternative only (SEO article shell stays in App.jsx).
 */

function fmtUsd(n) {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '2'
  return String(Math.round(n))
}

/**
 * @param {{
 *   t: (k: string, p?: object) => string,
 *   tierPricing: { basic?: { monthly?: number } },
 *   locPath: (p: string) => string,
 *   goToMarketingSeoPath: (e: unknown, p: string) => void,
 *   openSignupAuthModal: () => void,
 * }} props
 */
export function DocusignAlternativeTopBlocks({
  t,
  tierPricing,
  locPath,
  goToMarketingSeoPath,
  openSignupAuthModal,
}) {
  const basicMo = tierPricing?.basic?.monthly ?? 2

  return (
    <div className="docusign-alt-lp">
      <div className="docusign-alt-lp__hero-ctas">
        <button type="button" className="primary-button docusign-alt-lp__btn-primary" onClick={openSignupAuthModal}>
          {t('docusignAlt.ctaStartFree')}
        </button>
        <a
          href={locPath('/how-it-works')}
          className="ghost-button docusign-alt-lp__btn-ghost"
          onClick={(e) => goToMarketingSeoPath(e, '/how-it-works')}
        >
          {t('docusignAlt.ctaHowItWorks')}
        </a>
        <p className="docusign-alt-lp__hero-trust">{t('docusignAlt.lp.heroTrustLine')}</p>
      </div>

      <section className="docusign-alt-lp__compare" aria-labelledby="docusign-alt-lp-compare-title">
        <h2 id="docusign-alt-lp-compare-title" className="docusign-alt-lp__h2">
          {t('docusignAlt.lp.compareTitle')}
        </h2>
        <p className="docusign-alt-lp__lede">{t('docusignAlt.lp.compareLead')}</p>
        <div className="docusign-alt-lp__table-wrap">
          <table className="docusign-alt-lp__table">
            <caption className="docusign-alt-lp__table-caption">{t('docusignAlt.lp.compareCaption')}</caption>
            <thead>
              <tr>
                <th scope="col">{t('docusignAlt.lp.compareColTopic')}</th>
                <th scope="col">{t('docusignAlt.lp.compareColDocu')}</th>
                <th scope="col">{t('docusignAlt.lp.compareColPrime')}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">{t('docusignAlt.lp.compareRowCost')}</th>
                <td>{t('docusignAlt.lp.compareRowCostDocu')}</td>
                <td>{t('docusignAlt.lp.compareRowCostPrime', { price: fmtUsd(basicMo) })}</td>
              </tr>
              <tr>
                <th scope="row">{t('docusignAlt.lp.compareRowEase')}</th>
                <td>{t('docusignAlt.lp.compareRowEaseDocu')}</td>
                <td>{t('docusignAlt.lp.compareRowEasePrime')}</td>
              </tr>
              <tr>
                <th scope="row">{t('docusignAlt.lp.compareRowSetup')}</th>
                <td>{t('docusignAlt.lp.compareRowSetupDocu')}</td>
                <td>{t('docusignAlt.lp.compareRowSetupPrime')}</td>
              </tr>
              <tr>
                <th scope="row">{t('docusignAlt.lp.compareRowSmb')}</th>
                <td>{t('docusignAlt.lp.compareRowSmbDocu')}</td>
                <td>{t('docusignAlt.lp.compareRowSmbPrime')}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="docusign-alt-lp__footnote">{t('docusignAlt.lp.compareFootnote')}</p>
      </section>

      <section className="docusign-alt-lp__why" aria-labelledby="docusign-alt-lp-why-title">
        <h2 id="docusign-alt-lp-why-title" className="docusign-alt-lp__h2">
          {t('docusignAlt.lp.whyTitle')}
        </h2>
        <p className="docusign-alt-lp__prose">{t('docusignAlt.lp.whyLead')}</p>
        <ul className="docusign-alt-lp__bullet-list">
          <li>{t('docusignAlt.lp.whyLi1')}</li>
          <li>{t('docusignAlt.lp.whyLi2')}</li>
          <li>{t('docusignAlt.lp.whyLi3')}</li>
          <li>{t('docusignAlt.lp.whyLi4')}</li>
        </ul>
        <p className="docusign-alt-lp__prose docusign-alt-lp__prose--emph">{t('docusignAlt.lp.whyClosing')}</p>
      </section>

      <section className="docusign-alt-lp__personas" aria-labelledby="docusign-alt-lp-personas-title">
        <h2 id="docusign-alt-lp-personas-title" className="docusign-alt-lp__h2">
          {t('docusignAlt.lp.personasTitle')}
        </h2>
        <div className="docusign-alt-lp__persona-grid">
          {[
            { titleKey: 'docusignAlt.lp.personaFreelanceTitle', bodyKey: 'docusignAlt.lp.personaFreelanceBody' },
            { titleKey: 'docusignAlt.lp.personaSmbTitle', bodyKey: 'docusignAlt.lp.personaSmbBody' },
            { titleKey: 'docusignAlt.lp.personaAgencyTitle', bodyKey: 'docusignAlt.lp.personaAgencyBody' },
            { titleKey: 'docusignAlt.lp.personaLandlordTitle', bodyKey: 'docusignAlt.lp.personaLandlordBody' },
            { titleKey: 'docusignAlt.lp.personaConsultTitle', bodyKey: 'docusignAlt.lp.personaConsultBody' },
          ].map((card) => (
            <article key={card.titleKey} className="docusign-alt-lp__persona-card">
              <h3 className="docusign-alt-lp__persona-title">{t(card.titleKey)}</h3>
              <p className="docusign-alt-lp__persona-body">{t(card.bodyKey)}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="docusign-alt-lp__proof" aria-labelledby="docusign-alt-lp-proof-title">
        <h2 id="docusign-alt-lp-proof-title" className="docusign-alt-lp__h2">
          {t('docusignAlt.lp.proofTitle')}
        </h2>
        <p className="docusign-alt-lp__quote">{t('docusignAlt.lp.proofQuote')}</p>
        <ul className="docusign-alt-lp__trust-chips">
          <li>{t('docusignAlt.lp.proofChip1')}</li>
          <li>{t('docusignAlt.lp.proofChip2')}</li>
          <li>{t('docusignAlt.lp.proofChip3')}</li>
        </ul>
      </section>

      <section className="docusign-alt-lp__steps" aria-labelledby="docusign-alt-lp-steps-title">
        <h2 id="docusign-alt-lp-steps-title" className="docusign-alt-lp__h2">
          {t('docusignAlt.lp.stepsTitle')}
        </h2>
        <ol className="docusign-alt-lp__steps-list">
          <li>
            <span className="docusign-alt-lp__step-num">1</span>
            <div>
              <strong className="docusign-alt-lp__step-title">{t('docusignAlt.lp.step1Title')}</strong>
              <p className="docusign-alt-lp__step-desc">{t('docusignAlt.lp.step1Desc')}</p>
            </div>
          </li>
          <li>
            <span className="docusign-alt-lp__step-num">2</span>
            <div>
              <strong className="docusign-alt-lp__step-title">{t('docusignAlt.lp.step2Title')}</strong>
              <p className="docusign-alt-lp__step-desc">{t('docusignAlt.lp.step2Desc')}</p>
            </div>
          </li>
          <li>
            <span className="docusign-alt-lp__step-num">3</span>
            <div>
              <strong className="docusign-alt-lp__step-title">{t('docusignAlt.lp.step3Title')}</strong>
              <p className="docusign-alt-lp__step-desc">{t('docusignAlt.lp.step3Desc')}</p>
            </div>
          </li>
        </ol>
      </section>

      <p className="docusign-alt-lp__internal docusign-alt-landing__prose">
        {t('docusignAlt.lp.internalBeforePricing')}{' '}
        <a href={locPath('/pricing')} onClick={(e) => goToMarketingSeoPath(e, '/pricing')}>
          {t('docusignAlt.lp.internalPricingLink')}
        </a>
        {t('docusignAlt.lp.internalBetween1')}{' '}
        <a href={locPath('/how-it-works')} onClick={(e) => goToMarketingSeoPath(e, '/how-it-works')}>
          {t('docusignAlt.lp.internalHowLink')}
        </a>
        {t('docusignAlt.lp.internalBetween2')}{' '}
        <a href={locPath('/security')} onClick={(e) => goToMarketingSeoPath(e, '/security')}>
          {t('docusignAlt.lp.internalSecurityLink')}
        </a>
        {t('docusignAlt.lp.internalAfter')}
      </p>
    </div>
  )
}

/** Final CTA band before FAQ */
export function DocusignAlternativeBottomCta({ t, openSignupAuthModal }) {
  return (
    <section className="docusign-alt-lp__bottom" aria-labelledby="docusign-alt-lp-bottom-title">
      <h2 id="docusign-alt-lp-bottom-title" className="docusign-alt-lp__bottom-title">
        {t('docusignAlt.lp.bottomTitle')}
      </h2>
      <p className="docusign-alt-lp__bottom-sub">{t('docusignAlt.lp.bottomSub')}</p>
      <button type="button" className="primary-button docusign-alt-lp__bottom-btn" onClick={openSignupAuthModal}>
        {t('docusignAlt.ctaStartFree')}
      </button>
    </section>
  )
}

/**
 * @param {{ enabled: boolean, t: (k: string) => string, openSignupAuthModal: () => void }} props
 */
export function DocusignAlternativeStickyBar({ enabled, t, openSignupAuthModal }) {
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return undefined
    const onScroll = () => setVisible(window.scrollY > 420)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [enabled])
  if (!enabled || !visible) return null
  return (
    <div className="docusign-alt-lp-sticky" role="region" aria-label={t('docusignAlt.lp.stickyAria')}>
      <p className="docusign-alt-lp-sticky__text">{t('docusignAlt.lp.stickyLine')}</p>
      <button type="button" className="primary-button docusign-alt-lp-sticky__btn" onClick={openSignupAuthModal}>
        {t('docusignAlt.ctaStartFree')}
      </button>
    </div>
  )
}
